import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import Axios from 'axios';
import moment from 'moment';
import { Button, Form } from 'react-bootstrap';
import qs from 'qs';
import { Icon, LoadingAnimation } from './Utilities';
import UserPill from './UserPill';
import SafeHtml from './SafeHtml';
import { getPutPostConfig } from '../ResourceApi';

// Privacy level constants
// Note: "Subject" = the person the note is about (the user being viewed, model_id)
//       "Subject's Manager" = that person's manager
//       The note writer (author_id) does NOT affect who can see the note
const PRIVACY_LEVELS = {
  SMT_MANAGER_USER: { value: 0, label: 'Visible to Ops, Subject\'s Manager and Subject' },
  SMT_MANAGER: { value: 1, label: 'Visible to Ops and Subject\'s Manager' },
  MANAGER_USER: { value: 2, label: 'Visible to Subject\'s Manager and Subject' },
  MANAGER: { value: 3, label: 'Visible to Subject\'s Manager Only' },
};

function Notes({
  modelType, modelId, sessionUser, relatedUser,
}) {
  const [notes, setNotes] = useState([]);
  const [note, setNote] = useState('');
  const [privacy, setPrivacy] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draftNoteId, setDraftNoteId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const draftSavedTimerRef = useRef(null);
  const isPublishingRef = useRef(false);
  const textareaRef = useRef(null);

  // Determine if this is a "broad visibility" model (Projects, Deployments, Candidates)
  // or a "privacy-filtered" model (Users, Objectives)
  const broadModels = ['App\\Project', 'App\\Deployment', 'App\\Candidate'];
  const isBroadVisibilityModel = broadModels.includes(modelType);

  // Derive actual modelId to use for the API call
  // For broad visibility models, use modelId directly
  // For privacy-filtered models (User, Objective), use relatedUser.id if modelId not provided
  const resolvedModelId = modelId !== undefined ? modelId : (relatedUser?.id);

  const loadNotes = async () => {
    setLoading(true);

    try {
      const response = await Axios.get('/notes', {
        params: {
          model: modelType,
          model_id: resolvedModelId,
        },
        paramsSerializer: (params) => qs.stringify(params),
      });

      const loadedNotes = response.data;
      setNotes(loadedNotes);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadNotes(); }, [modelType, modelId, relatedUser]);

  // Clean up the draft-saved timer on unmount
  useEffect(() => () => {
    if (draftSavedTimerRef.current) {
      clearTimeout(draftSavedTimerRef.current);
    }
  }, []);

  const showDraftSavedIndicator = useCallback(() => {
    setDraftSaved(true);
    if (draftSavedTimerRef.current) {
      clearTimeout(draftSavedTimerRef.current);
    }
    draftSavedTimerRef.current = setTimeout(() => setDraftSaved(false), 3000);
  }, []);

  const editDraft = useCallback((draftNote) => {
    setNote(draftNote.raw_note || '');
    setDraftNoteId(draftNote.id);
    setPrivacy(draftNote.privacy ?? 0);
    if (textareaRef.current) {
      textareaRef.current.focus();
    }
  }, []);

  const saveAsDraft = useCallback(async () => {
    if (!note || isPublishingRef.current) return;

    try {
      let response;
      if (draftNoteId) {
        response = await Axios.patch(`/notes/${draftNoteId}`, {
          note,
          privacy: isBroadVisibilityModel ? 0 : privacy,
          is_draft: true,
        }, getPutPostConfig());
      } else {
        response = await Axios.post('/notes', {
          model: modelType,
          model_id: resolvedModelId,
          note,
          privacy: isBroadVisibilityModel ? 0 : privacy,
          is_draft: true,
        }, getPutPostConfig());
      }

      const updatedNotes = response.data;
      setNotes(updatedNotes);

      // Track the draft note id so subsequent blur saves update it
      if (!draftNoteId) {
        const savedDraft = updatedNotes.find(
          (n) => n.is_draft && n.raw_note !== null && n.raw_note !== undefined,
        );
        if (savedDraft) {
          setDraftNoteId(savedDraft.id);
        }
      }

      showDraftSavedIndicator();
    } catch (error) {
      console.error(error);
    }
  }, [note, draftNoteId, privacy, modelType, resolvedModelId, showDraftSavedIndicator]);

  const handleBlur = useCallback(() => {
    saveAsDraft();
  }, [saveAsDraft]);

  const clearForm = useCallback(() => {
    setNote('');
    setPrivacy(0);
    setDraftNoteId(null);
    setDraftSaved(false);
  }, []);

  const saveNote = async (event) => {
    event.preventDefault();
    isPublishingRef.current = true;
    setLoading(true);

    try {
      let response;
      if (draftNoteId) {
        // Publish the existing draft
        response = await Axios.patch(`/notes/${draftNoteId}`, {
          note,
          privacy: isBroadVisibilityModel ? 0 : privacy,
          is_draft: false,
        }, getPutPostConfig());
      } else {
        // Create a new published note directly
        response = await Axios.post('/notes', {
          model: modelType,
          model_id: resolvedModelId,
          note,
          privacy: isBroadVisibilityModel ? 0 : privacy,
          is_draft: false,
        }, getPutPostConfig());
      }

      setNotes(response.data);
      clearForm();
    } catch (error) {
      console.error(error);
    } finally {
      isPublishingRef.current = false;
      setLoading(false);
    }
  };

  const doDeleteNote = async (noteId) => {
    setLoading(true);

    try {
      const response = await Axios.delete(`/notes/${noteId}`);
      setNotes(response.data);

      // If the deleted note was the current draft, clear the form
      if (noteId === draftNoteId) {
        clearForm();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const deleteNote = (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      doDeleteNote(noteId);
    }
  };

  // Determine which privacy options are available for the session user to CREATE
  // Based on their relationship to the related user (for privacy-filtered models only)
  const getAvailablePrivacyOptions = () => {
    if (!sessionUser || !relatedUser) {
      return [PRIVACY_LEVELS.SMT_MANAGER_USER];
    }

    const options = [];

    // Level 0: Always available to anyone who can create notes
    // (visible to Ops, Subject's Manager, and Subject)
    options.push(PRIVACY_LEVELS.SMT_MANAGER_USER);

    // Level 1: Only Ops can create (hides Subject from seeing it)
    // "Visible to Ops and Subject's Manager"
    if (sessionUser.isOps) {
      options.push(PRIVACY_LEVELS.SMT_MANAGER);
    }

    // Level 2: Subject's Manager and the Subject themselves can create
    // "Visible to Subject's Manager and Subject"
    // This is for private notes between manager and report
    if (sessionUser.id === relatedUser.id || sessionUser.id === relatedUser.manager_id) {
      options.push(PRIVACY_LEVELS.MANAGER_USER);
    }

    // Level 3: Only Subject's Manager can create
    // "Visible to Subject's Manager Only"
    // This is for completely confidential manager notes
    if (sessionUser.id === relatedUser.manager_id) {
      options.push(PRIVACY_LEVELS.MANAGER);
    }

    return options;
  };

  // Determine if session user can create notes
  const canCreateNotes = () => {
    // For broad visibility models, assume anyone can add notes
    if (isBroadVisibilityModel) {
      return true;
    }

    // For privacy-filtered models, check user relationships
    if (!sessionUser || !relatedUser) {
      return false;
    }

    // User can create notes for themselves
    if (sessionUser.id === relatedUser.id) {
      return true;
    }

    // Manager can create notes for their team
    if (sessionUser.id === relatedUser.manager_id) {
      return true;
    }

    // Ops/Admin can create notes
    if (sessionUser.isOps) {
      return true;
    }

    return false;
  };

  const availablePrivacyOptions = getAvailablePrivacyOptions();
  const showNoteForm = canCreateNotes();
  const showPrivacySelect = !isBroadVisibilityModel;

  return (
    <div className="p-4" style={{ position: 'relative' }}>
      <LoadingAnimation loading={loading} />

      {showNoteForm && (
        <Form onSubmit={saveNote} className="mb-4">
          <Form.Group>
            <Form.Control
              as="textarea"
              ref={textareaRef}
              rows={3}
              onChange={(event) => setNote(event.target.value)}
              onBlur={handleBlur}
              value={note}
              placeholder="Add a note..."
            />
          </Form.Group>

          {showPrivacySelect && (
            <Form.Group>
              <Form.Label column="sm">Visibility</Form.Label>
              <Form.Control
                as="select"
                value={privacy}
                onChange={(event) => setPrivacy(parseInt(event.target.value, 10))}
              >
                {availablePrivacyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          )}

          <div className="row align-items-center">
            <div className="col-md-6">
              {draftSaved && (
                <span className="text-muted small">
                  <Icon icon="check" />
                  {' '}
                  Draft saved
                </span>
              )}
            </div>
            <div className="col-md-6" style={{ textAlign: 'right' }}>
              {draftNoteId && (
                <Button
                  variant="link"
                  size="sm"
                  className="mr-2 text-muted"
                  onClick={clearForm}
                >
                  New note
                </Button>
              )}
              <Button type="submit" disabled={!note}>Publish</Button>
            </div>
          </div>
        </Form>
      )}

      <div className="notes-list">
        {notes.map((existingNote) => (
          <Note
            key={existingNote.id}
            note={existingNote}
            deleteNote={deleteNote}
            editDraft={editDraft}
            showPrivacy={showPrivacySelect}
          />
        ))}
      </div>
    </div>
  );
}

Notes.propTypes = {
  modelType: PropTypes.string.isRequired,
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sessionUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isOps: PropTypes.bool,
  }),
  relatedUser: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    manager_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
};

Notes.defaultProps = {
  modelId: undefined,
  sessionUser: null,
  relatedUser: null,
};

export function Note({
  note, deleteNote, editDraft, showPrivacy,
}) {
  // Get the privacy label based on the note's privacy level
  const getPrivacyLabel = () => {
    if (!showPrivacy) {
      return null;
    }

    const level = Object.values(PRIVACY_LEVELS).find((pl) => pl.value === note.privacy);
    return level ? level.label : 'Unknown visibility';
  };

  const privacyLabel = getPrivacyLabel();

  return (
    <div className={`note mb-4${note.is_draft ? ' note--draft' : ''}`}>
      {!!note.is_draft && (
        <div className="note-draft-badge mb-1">
          <span className="badge badge-warning">Draft – only visible to you</span>
        </div>
      )}
      <SafeHtml className="note-body" html={note.display_note} />
      <div className="note-meta">
        {!!note.is_draft && note.raw_note !== null && note.raw_note !== undefined && editDraft && (
          <Button
            variant="secondary"
            size="sm"
            className="float-right ml-2"
            onClick={() => editDraft(note)}
          >
            <Icon icon="edit" />
            {' '}
            Edit
          </Button>
        )}
        {note.deletable && (
          <Button className="delete float-right" onClick={() => deleteNote(note.id)}>
            <Icon icon="trash" />
            {' '}
            Delete
          </Button>
        )}
        <div style={{ display: 'inline-block', verticalAlign: 'middle', marginRight: '15px' }}>
          <UserPill user={note.author} />
        </div>
        <div className="small text-muted">
          {moment(note.created_at).format('D MMMM YYYY')}
        </div>
        {privacyLabel && (
          <div className="small text-muted" style={{ marginTop: '8px', fontStyle: 'italic' }}>
            {privacyLabel}
          </div>
        )}
      </div>
    </div>
  );
}

Note.propTypes = {
  note: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    display_note: PropTypes.string.isRequired,
    is_draft: PropTypes.bool,
    raw_note: PropTypes.string,
    deletable: PropTypes.bool,
    privacy: PropTypes.number,
    author: PropTypes.shape({ name: PropTypes.string.isRequired }).isRequired,
    created_at: PropTypes.string.isRequired,
  }).isRequired,
  deleteNote: PropTypes.func.isRequired,
  editDraft: PropTypes.func,
  showPrivacy: PropTypes.bool,
};

Note.defaultProps = {
  editDraft: null,
  showPrivacy: true,
};

export default Notes;
